<script>
import {ref} from "vue";
import {IMaskDirective} from 'vue-imask';
import IconX from "@js/components/Icons/IconX.vue";
import CallbackErrorMessage from "@js/components/Statuses/CallbackErrorMessage.vue";
import SpinnerIndicator from "@js/components/Statuses/SpinnerIndicator.vue";
import {useCallbackStore} from "@js/store/CallbackStore.js";
import PrivacyPolicyAgreementLink from "@js/components/Sections/Links/PrivacyPolicyAgreementLink.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import IconAnimatedCheckMark from "@js/components/Icons/IconAnimatedCheckMark.vue";
import ImageLoader from "@js/components/Loaders/ImageLoader.vue";
import baseImg from '@img/callback/base.webp'

export default {
    name: "BaseCallback",
    components: {
        ImageLoader,
        IconAnimatedCheckMark,
        OnlyMobile,
        OnlyDesktop,
        PrivacyPolicyAgreementLink,
        SpinnerIndicator,
        CallbackErrorMessage,
        IconX,
    },
    directives: {
        imask: IMaskDirective
    },
    data() {
        return {
            phoneMask: {
                mask: '+7 (000) 000-00-00',
            },
        }
    },
    methods: {
        updatePhone(newPhone) {
            this.callbackStore.base_form.data.phone = newPhone;
        },
    },
    setup() {
        const callbackStore = useCallbackStore()
        const modalRef = ref(null)

        const closeModal = () => {
            callbackStore.closeCallbackPopup()
        }

        const handleBackgroundClick = (event) => {
            if (!modalRef.value.contains(event.target)) {
                closeModal()
            }
        }

        // Обработка свайпа вниз
        const touchStartY = ref(0);
        const touchDeltaY = ref(0);
        const onTouchStart = (e) => {
            touchStartY.value = e.touches[0].clientY;
        };
        const onTouchMove = (e) => {
            touchDeltaY.value = e.touches[0].clientY - touchStartY.value;
            if (touchDeltaY.value > 0 && modalRef.value) {
                modalRef.value.style.transform = `translateY(${touchDeltaY.value}px)`;
            }
        };
        const onTouchEnd = () => {
            if (touchDeltaY.value > 100) {
                closeModal();
            } else if (modalRef.value) {
                // Вернуть в исходное положение
                modalRef.value.style.transition = "transform 0.3s";
                modalRef.value.style.transform = "translateY(0)";
                setTimeout(() => {
                    if (modalRef.value) {
                        modalRef.value.style.transition = "";
                    }
                }, 300);
            }
            touchDeltaY.value = 0;
        };

        return {
            callbackStore,
            modalRef,
            closeModal,
            handleBackgroundClick,
            onTouchStart,
            onTouchMove,
            onTouchEnd,
            baseImg,
        }
    },
}
</script>

<template>
    <div v-if="callbackStore.base_form.isOpen"
         class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-70 transition-opacity "
         @click="handleBackgroundClick">
        <div ref="modalRef"
             @touchstart.passive="onTouchStart"
             @touchmove.passive="onTouchMove"
             @touchend="onTouchEnd"
             :class="{'top-i-0': callbackStore.base_form.isOpen && callbackStore.base_form.animation_in_progress}"
             class="relative top-[100%] bg-white bg-contain bg-no-repeat bg-center rounded-tr-2xl rounded-tl-2xl sm:rounded-2xl shadow-lg max-h-[70%] sm:max-w-[800px] sm:max-h-[450px] w-full h-full transition-all duration-500 overflow-hidden">
            <button @click.prevent="closeModal"
                    class="absolute top-2 right-2 text-gray-400 md:hover:text-white focus:outline-none">
                <IconX
                    class="h-7 w-7 stroke-[#000] active:stroke-baseColor sm:hover:stroke-baseColor transition"/>
            </button>
            <OnlyMobile :maxSize="1024" class="absolute top-3 right-0 left-0 m-auto flex justify-center">
                    <div class="h-[5px] w-[50px] bg-gray-400 rounded-full"></div>
            </OnlyMobile>
            <div class="w-full h-full flex items-start sm:items-center justify-center sm:justify-between pt-16 sm:pt-0">
                <OnlyDesktop :maxSize="1024" class="w-[400px] h-full">
                    <ImageLoader title="Обратный звонок"
                                 :src="callbackStore.base_form.img_src || baseImg"
                                 imgClasses="w-full h-full object-cover object-center"/>
                </OnlyDesktop>
                <div v-if="!callbackStore.base_form.successfully_sent"
                     class="w-full max-w-sm mx-auto p-3 sm:p-5">
                    <p class="text-2xl text-[#2D2D2D] font-bold text-center mb-8">
                        {{ callbackStore.base_form.title }}
                    </p>
                    <div>
                        <div class="relative z-0 w-full group">
                            <input type="tel"
                                   @keyup="updatePhone($event.target.value)"
                                   :value="callbackStore.base_form.data.phone"
                                   v-imask="phoneMask"
                                   name="ptf_phone" id="ptf_phone"
                                   class="block py-2.5 px-0 w-full text-gray-900 bg-transparent border-0 border-b-2 border-black appearance-none  focus:outline-none focus:ring-0 focus:border-black peer"
                                   :class="[callbackStore.base_form.errors && callbackStore.base_form.errors['phone'] ? 'border-red-500' : '']"
                                   placeholder=" " required/>
                            <label for="ptf_phone"
                                   class="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0  peer-focus:text-black peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6 left-0">
                                Номер телефона
                            </label>
                        </div>
                        <CallbackErrorMessage :error="callbackStore.base_form.errors"
                                              :error-msg="'phone'"/>
                    </div>
                    <div class="mt-5">
                        <div class="relative z-0 w-full  group">
                            <input v-model="callbackStore.base_form.data.full_name" type="text"
                                   name="ptf_full_name" id="ptf_full_name"
                                   class="block py-2.5 px-0 w-full text-gray-900 bg-transparent border-0 border-b-2 border-black appearance-none  focus:outline-none focus:ring-0 focus:border-black peer"
                                   :class="[callbackStore.base_form.errors && callbackStore.base_form.errors['full_name'] ? 'border-red-500' : '']"
                                   placeholder=" " required/>
                            <label for="ptf_full_name"
                                   class="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0  peer-focus:text-black peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6 left-0">
                                Как к вам обращаться?
                            </label>
                        </div>
                        <CallbackErrorMessage :error="callbackStore.base_form.errors"
                                              :error-msg="'full_name'"/>
                    </div>
                    <div class="relative w-full lg:w-auto flex justify-center mt-10">
                        <button @click.stop="callbackStore.sendPopupForm"
                                :disabled="callbackStore.base_form.sending"
                                class="flex justify-center items-center w-full text-black md:hover:text-baseColor border-2 border-baseColor bg-baseColor md:hover:bg-transparent font-medium rounded-full text-sm px-14 py-2.5 focus:outline-none transition cursor-pointer">
                            <span v-if="!callbackStore.base_form.sending" class="text-base">
                                Заказать звонок
                            </span>
                            <span v-else class="text-base">
                                Отправляем
                            </span>
                            <SpinnerIndicator v-if="callbackStore.base_form.sending"
                                              class="w-6 h-6 ms-2"/>
                        </button>
                    </div>
                    <div class="mt-5">
                        <PrivacyPolicyAgreementLink/>
                    </div>
                </div>
                <div v-else class="w-full max-w-sm mx-auto p-3 sm:p-5 flex flex-col items-center">
                   <IconAnimatedCheckMark size="80"/>
                    <p class="text-lg font-medium text-center text-[#7ac142] mt-5">
                        Спасибо!<br/> Мы скоро свяжемся с вами.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">

</style>
